import React, {
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';

import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import colors from '../../themes/main-colors';
import { Auth } from 'aws-amplify';

import PhoneDetails from './PhoneDetails';
import Enrollment from './Enrollment';
import EnrolledFactors from './EnrolledFactors';

import useAuthQuery from '../../hooks/useAuthQuery';
import { AccountCustomer } from '../../__generated__/pge-types';
import { parsePhoneNumber } from '../../util/format';
import { useTranslation } from '../../hooks/useTranslation';
import TrustedDeviceList from './TrustedDeviceList';
import { getContactDetails } from './queries';
import { featureFlagsContext } from '../../providers/FeatureFlagsProvider';

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiStepConnector-root': {
      marginLeft: '20px',
    },
    '& .MuiStepContent-root': {
      marginLeft: '20px',
      paddingLeft: '28px',
    },
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  actionsContainer: {
    marginBottom: theme.spacing(2),
  },
  resetContainer: {
    padding: theme.spacing(3),
  },
  completed: {
    color: colors.shoreGreen + ' !important',
  },
  stepper: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(3, 0),
    },
  },
  stepperLabel: {
    '& .MuiTypography-root': {
      fontSize: '1.2rem',
    },
  },
}));

const TwoStepAuthentication: FunctionComponent = props => {
  const [isMFAEnrolled, setIsMFAEnrolled] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<number>(0);

  const [MFAPhoneNumber, setMFAPhoneNumber] = useState<string>('');
  const [enrolledPhoneNumber, setEnrolledPhoneNumber] = useState<string>('');

  const classes = useStyles();
  const { t } = useTranslation();
  const { webTurnOnTrustedDevice, loading: isFeatureFlagLoading } = useContext(
    featureFlagsContext,
  );

  const loadUser = useCallback(async () => {
    try {
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const attributes = (user as { attributes?: Record<string, any> })
        .attributes;
      const preferredMfa = await Auth.getPreferredMFA(user).catch(
        () => undefined,
      );
      const userMfaSettings = (user as {
        userMFASettingList?: string[];
      }).userMFASettingList;

      const enrolled = Boolean(
        preferredMfa === 'SMS_MFA' ||
          preferredMfa === 'SOFTWARE_TOKEN_MFA' ||
          preferredMfa === 'TOTP' ||
          (Array.isArray(userMfaSettings) &&
            userMfaSettings.some(setting => setting && setting !== 'NOMFA')),
      );

      setIsMFAEnrolled(enrolled);
      setEnrolledPhoneNumber(attributes?.phone_number || '');

      setActiveStep(0);
    } catch (_error) {
      setIsMFAEnrolled(false);
      setEnrolledPhoneNumber('');
      setActiveStep(0);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  const refreshUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  const stepperLabelProps = {
    StepIconProps: {
      classes: {
        completed: classes.completed,
      },
    },
    className: classes.stepperLabel,
  };

  const { data } = useAuthQuery<{
    getAccountInfo: AccountCustomer;
  }>(getContactDetails, {
    variables: {
      params: {},
    },
  });

  const mobNumberData =
    data?.getAccountInfo?.contactDetails &&
    data?.getAccountInfo?.contactDetails?.find(
      mob => mob?.contactType === 'MOB',
    );
  const mobNumber = mobNumberData?.contactValue
    ? parsePhoneNumber(mobNumberData?.contactValue)
    : '';

  return (
    <Grid container spacing={2} className={classes.root}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant={'h2'} className={classes.title}>
                  {t('TWO_STEP_AUTH_ENROLLMENT_TITLE')}
                </Typography>
                <Typography variant={'body1'}>
                  {t('WHAT_IS_TWO_STEP_AUTHENTICATION')}
                </Typography>
              </Grid>
              {!isMFAEnrolled && (
                <Grid item>
                  <Stepper
                    className={classes.stepper}
                    activeStep={activeStep}
                    orientation="vertical"
                  >
                    <Step>
                      <StepLabel {...stepperLabelProps}>
                        <Typography variant={'h6'}>
                          {t('TWO_STEP_AUTH_ENTER_PHONE_NUMBER')}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <PhoneDetails
                          mobilePhone={mobNumber || ''}
                          onNext={(phone: string) => {
                            setMFAPhoneNumber(phone);
                            setActiveStep(1);
                          }}
                        />
                      </StepContent>
                    </Step>
                    <Step>
                      <StepLabel {...stepperLabelProps}>
                        <Typography variant={'h6'}>
                          {t('TWO_STEP_AUTH_ENROLL')}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Enrollment
                          phoneNumber={MFAPhoneNumber}
                          onVerify={async () => {
                            await refreshUser();
                            setActiveStep(0);
                          }}
                        />
                      </StepContent>
                    </Step>
                  </Stepper>
                </Grid>
              )}
            </Grid>
            <Box id="two-step-enrollment"></Box>
          </CardContent>
        </Card>

        {isMFAEnrolled && (
          <EnrolledFactors
            enrolledPhoneNumber={enrolledPhoneNumber}
            refreshUser={refreshUser}
          />
        )}

        {isMFAEnrolled && !isFeatureFlagLoading && webTurnOnTrustedDevice && (
          <TrustedDeviceList />
        )}
      </Grid>
    </Grid>
  );
};

export default TwoStepAuthentication;
